import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { WindowRow } from './WindowRow';
import { PricingSummary } from './PricingSummary';
import { ExportActions } from './ExportActions';
import { createDefaultAdderSelection, calculateAdderTotal } from './AdderSelector';
import { calculateBasePrice } from './pricingUtils';
import type { WindowData } from './WindowRow';
interface ProductLine {
  name: string;
  redline: number;
}
const PRODUCT_LINES: ProductLine[] = [{
  name: '80 Series',
  redline: 1700
}, {
  name: 'Mezzo',
  redline: 1750
}, {
  name: 'International',
  redline: 1900
}, {
  name: 'Western',
  redline: 2500
}, {
  name: 'Impervia',
  redline: 2450
}];
function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}
function createEmptyWindow(): WindowData {
  return {
    id: generateId(),
    qty: 1,
    width: 0,
    height: 0,
    glass: 'No',
    operations: [],
    adders: createDefaultAdderSelection(),
    included: true
  };
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
function formatCurrency2(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
export function WindowCalculator() {
  const [selectedProductLine, setSelectedProductLine] = useState<string>(PRODUCT_LINES[0].name);
  const [windows, setWindows] = useState<WindowData[]>([createEmptyWindow()]);
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const currentProduct = PRODUCT_LINES.find(p => p.name === selectedProductLine) ?? PRODUCT_LINES[0];
  function handleAddWindow() {
    setWindows(prev => [...prev, createEmptyWindow()]);
  }
  function handleUpdateWindow(id: string, updates: Partial<WindowData>) {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      ...updates
    } : w));
  }
  function handleRemoveWindow(id: string) {
    setWindows(prev => prev.filter(w => w.id !== id));
  }
  const includedWindows = windows.filter(w => w.included);
  const totalWindowCount = includedWindows.reduce((sum, w) => sum + w.qty, 0);
  const totalBasePrice = includedWindows.reduce((sum, w) => sum + calculateBasePrice(w.width, w.height, currentProduct.name) * w.qty, 0);
  const totalAdderCost = includedWindows.reduce((sum, w) => sum + calculateAdderTotal(w.adders) * w.qty, 0);
  const grandTotal = totalBasePrice + totalAdderCost;
  // Calculate max discount % so avg per window stays >= redline
  const maxDiscountPercent = grandTotal > 0 && totalWindowCount > 0 ? Math.max(0, (1 - currentProduct.redline * totalWindowCount / grandTotal) * 100) : 100;
  const clampedDiscount = Math.min(discountPercent, maxDiscountPercent);
  const discountAmount = grandTotal * (clampedDiscount / 100);
  const totalDue = grandTotal - discountAmount;
  const avgPerWindow = totalWindowCount > 0 ? totalDue / totalWindowCount : 0;
  const isAtRedlineLimit = discountPercent > 0 && clampedDiscount < discountPercent;
  return <div className="min-h-screen w-full bg-gray-100" data-id="element-67">
      {/* Header */}
      <header className="bg-[#2E5E3F] shadow-sm" data-id="element-68">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between" data-id="element-69">
          <div data-id="element-70">
            <h1 className="text-xl font-bold text-white tracking-tight" data-id="element-71">
              Window Pricing Calculator
            </h1>
            <p className="text-emerald-200 text-sm mt-0.5" data-id="element-72">
              Sales quoting tool
            </p>
          </div>
          <div className="text-right text-sm text-emerald-100" data-id="element-73">
            {totalWindowCount} window{totalWindowCount !== 1 ? 's' : ''}{' '}
            &middot;{' '}
            <span className="font-semibold text-white" data-id="element-74">
              {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2
            }).format(totalDue)}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-id="element-75">
        {/* Product Line Selector — no redline shown */}
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-4 flex flex-wrap items-center gap-4" data-id="element-76">
          <label htmlFor="product-line" className="text-sm font-medium text-gray-700" data-id="element-77">
            Product Line
          </label>
          <select id="product-line" value={selectedProductLine} onChange={e => setSelectedProductLine(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[180px]" data-id="element-78">
            {PRODUCT_LINES.map(pl => <option key={pl.name} value={pl.name} data-id="element-79">
                {pl.name}
              </option>)}
          </select>
        </div>

        {/* Windows Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-80">
          <div className="overflow-x-auto" data-id="element-81">
            <table className="w-full" data-id="element-82">
              <thead data-id="element-83">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-84">
                  <th className="px-2 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-10" data-id="element-85">
                    <span className="sr-only" data-id="element-86">Include</span>✓
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-16" data-id="element-87">
                    #
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-88">
                    Qty
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-89">
                    Width (in)
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-90">
                    Height (in)
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-91">
                    Sq Ft
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-92">
                    Base Price
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-93">
                    Operation
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-94">
                    Obscured
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-95">
                    Adders
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-96">
                    Adder Cost
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-97">
                    Line Total
                  </th>
                  <th className="px-3 py-3 w-12" data-id="element-98"></th>
                </tr>
              </thead>
              <tbody data-id="element-99">
                {windows.map((win, idx) => <WindowRow key={win.id} window={win} index={idx} productLine={currentProduct.name} onUpdate={handleUpdateWindow} onRemove={handleRemoveWindow} canRemove={windows.length > 1} data-id="element-100" />)}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50" data-id="element-101">
            <button type="button" onClick={handleAddWindow} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#2E5E3F] bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors" data-id="element-102">
              <PlusIcon className="w-4 h-4" data-id="element-103" />
              Add Window
            </button>
          </div>
        </div>

        {/* Summary — no redline visible to customer */}
        <PricingSummary windowCount={totalWindowCount} totalBasePrice={totalBasePrice} totalAdderCost={totalAdderCost} grandTotal={grandTotal} productLine={currentProduct.name} discountPercent={clampedDiscount} onDiscountChange={setDiscountPercent} discountAmount={discountAmount} totalDue={totalDue} isAtRedlineLimit={isAtRedlineLimit} data-id="element-104" />

        {/* Export Actions */}
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-4" data-id="element-105">
          <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-106">
            <div data-id="element-107">
              <h3 className="text-sm font-semibold text-gray-800" data-id="element-108">
                Export Quote
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-109">
                Copy to paste into your Go High Level contract, or download as
                CSV
              </p>
            </div>
            <ExportActions windows={includedWindows} productLine={currentProduct.name} totalBasePrice={totalBasePrice} totalAdderCost={totalAdderCost} grandTotal={grandTotal} discountPercent={clampedDiscount} discountAmount={discountAmount} totalDue={totalDue} totalWindowCount={totalWindowCount} avgPerWindow={avgPerWindow} data-id="element-110" />
          </div>
        </div>

        {/* Redline — below export, away from customer view */}
        <div className="bg-gray-50/60 border border-gray-100 rounded-lg px-6 py-4" data-id="element-111">
          <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-112">
            <div data-id="element-113">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-114">
                Rep Only — Redline
              </h3>
              <p className="text-[11px] text-gray-300 mt-0.5" data-id="element-115">
                Minimum avg price per window for {currentProduct.name}
              </p>
            </div>
            <div className="text-right" data-id="element-116">
              <p className="text-sm font-medium text-gray-400 tabular-nums" data-id="element-117">
                {formatCurrency(currentProduct.redline)}/window
              </p>
              <p className="text-[11px] text-gray-300 mt-1 tabular-nums" data-id="element-118">
                Avg $/Window: {formatCurrency2(avgPerWindow)}
              </p>
              {isAtRedlineLimit && <p className="text-[11px] text-gray-400 mt-1" data-id="element-119">
                  Discount capped
                </p>}
            </div>
          </div>
        </div>
      </main>
    </div>;
}